import { Component, EventEmitter, HostListener, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { TenantScheduleModalService } from 'src/app/modules/platform/schedules/schedule-modal/tenant-schedule-modal.service';
import { TenantSchedule } from '../../models/tenant-schedule';
import { parseSearchTokens } from '../../utils/string.utils';
import BaseComponent from '../base.component';
import { SelectableItem } from '../grid-bulk-actions/grid-bulk-actions.component';
import { TenantScheduleSummary } from '../../models/tenant-schedule-summary';
import { CronService } from '../cron-editor/cron.service';

export interface ScheduleVM extends SelectableItem {
    name?: string;
    scheduleDesc?: string;
    isActive?: boolean;
    scheduleId?: number;
    rawCron?: string;
    ingestionJobCount?: number;
}

@Component({
    selector: 'cdp-schedule-select',
    templateUrl: './schedule-select.component.html',
    styleUrls: ['./schedule-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ScheduleSelectComponent),
            multi: true
        }
    ],
    standalone: false
})
export class ScheduleSelectComponent extends BaseComponent implements ControlValueAccessor {
    @ViewChild(NgSelectComponent) ngSelect: NgSelectComponent;
    /** show loading indicator */
    @Input() isLoading = false;
    /** if true, when selecting an inactive schedule, show warning in the field rather than below it */
    @Input() showInactiveWarningAsIcon = false;
    /** if true, include a footer with a button to add a new schedule (don't use this when working in a modal) */
    @Input() showNewSchedBtn = false;
    /** if true, the dropdown will be thinner */
    @Input() isTight = false;
    /** needed for constructing links to the schedules */
    @Input() tenantId = '';
    @Input() hasSourcesEditPermission = false;
    @Input() selectedScheduleName = '';
    /** the schedule options */
    @Input() set schedules(schedules: TenantScheduleSummary[]) {
        this.processSchedules(schedules);
        this._schedules = schedules;
        this.setCurrentSchedTooltip();
    }
    get schedules(): TenantScheduleSummary[] {
        return this._schedules;
    }
    @Output() newScheduleCreated = new EventEmitter<TenantSchedule>();
    @Output() scheduleChange = new EventEmitter<ScheduleVM>();
    private _schedules: TenantScheduleSummary[] = [];
    public scheduleId: number;
    public isInactiveScheduleSelected = false;
    readonly inactiveScheduleWarning =
        'The selected schedule is inactive. This job will not run unless manually queued or the schedule is activated.';

    // ControlValueAccessor
    public onChange: (value: any) => void = () => undefined;
    public onTouch: () => any = () => undefined;
    public writeValue(model: number): void {
        this.scheduleId = model;
        this.setCurrentSchedTooltip();
        this.checkForInactiveSchedule();
    }
    public registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => any): void {
        this.onTouch = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    @HostListener('keydown', ['$event'])
    public onKeydown($event: KeyboardEvent): void {
        $event.stopPropagation();
    }

    scheduleOptions!: ScheduleVM[];
    currentSchedTooltip = '';
    isDisabled = false;

    constructor(
        private schedModal: TenantScheduleModalService,
        private cronService: CronService
    ) {
        super();
    }

    onChangeSchedule(selection: ScheduleVM) {
        this.onChange(selection?.scheduleId);
        this.scheduleChange.emit(selection);
        this.setCurrentSchedTooltip();
        this.checkForInactiveSchedule();
    }

    schedSearchFn(term: string, item: ScheduleVM): boolean {
        const tokens = parseSearchTokens(term);
        const searchIn = `${item.name} ${item.scheduleDesc}`.toLocaleLowerCase();
        return tokens.every(token => searchIn.indexOf(token) > -1);
    }

    createNewSched() {
        this.ngSelect.close();
        this.schedModal.open(this.tenantId).subscribe(data => {
            this.scheduleOptions = [...this.scheduleOptions, this.convertScheduleToVM(data)];
            this.scheduleId = data.tenantScheduleId;
            this.onChange(data.tenantScheduleId);
            this.newScheduleCreated.emit(data);
        });
    }

    public convertScheduleToVM(schedule: TenantScheduleSummary): ScheduleVM {
        return {
            name: schedule.description || 'Unnamed Schedule',
            scheduleDesc: this.cronService.translateCronWithUtc(schedule.cron),
            isActive: schedule.activeFlag,
            scheduleId: schedule.tenantScheduleId,
            isSelected: false,
            rawCron: schedule.cron,
            ingestionJobCount: schedule.ingestionJobCount
        };
    }

    private processSchedules(data: TenantScheduleSummary[]) {
        this.scheduleOptions = data.map(s => this.convertScheduleToVM(s));
    }

    private setCurrentSchedTooltip() {
        const selection = this.scheduleOptions.find(s => s.scheduleId === this.scheduleId);
        this.currentSchedTooltip = selection
            ? `(${selection?.isActive ? 'Active' : 'Inactive'}) ${selection?.name}`
            : '';
    }

    private checkForInactiveSchedule() {
        const selectedSched = this.scheduleOptions.find(s => s.scheduleId === this.scheduleId);
        this.isInactiveScheduleSelected = !!selectedSched && !selectedSched?.isActive;
    }

    getSelectedScheduleName(item?: ScheduleVM): string {
        return item?.name || this.selectedScheduleName || 'No Schedule';
    }

    isSelectedScheduleActive(item?: ScheduleVM): boolean {
        return item?.isActive ?? true;
    }
}
